<template>
  <div class="pay-method-card" :class="cardClass" @click="handleClick">
    <div v-if="showIcon" class="pay-method-card__visual">
      <div class="pay-method-card__icon">
        <div v-if="resolvedIconType === 'cod'" class="box-icon">
          <span></span>
        </div>
        <div v-else-if="resolvedIconType === 'store'" class="store-icon" aria-hidden="true">
          <span></span>
        </div>
        <div v-else-if="resolvedIconType === 'wallet'" class="wallet-icon" aria-hidden="true">
          <span></span>
        </div>
        <div v-else class="qris-icon" aria-hidden="true">
          <span v-for="item in 49" :key="item"></span>
        </div>
      </div>
    </div>
    <div class="pay-method-card__body">
      <div class="pay-method-card__header">
        <div class="pay-method-card__title">{{ title }}</div>
      </div>
      <div v-if="selected && recommended" class="pay-method-card__tag">
        {{ recommendedText || $t('orderPage.codRecommended') }}
      </div>
      <div class="pay-method-card__desc">{{ desc }}</div>
      <div v-if="tags && tags.length" class="pay-method-card__tags">
        <span v-for="tag in tags" :key="tag" class="pay-method-tag">
          <van-icon :name="tagIconName(tag)" class="pay-method-tag__icon" />
          <span>{{ tag }}</span>
        </span>
      </div>
      <div v-if="logos && logos.length" class="pay-method-card__logos">
        <span v-for="logo in logoItems" :key="logo.raw" class="channel-logo" :class="`channel-logo--${logo.key}`">
          <img v-if="logo.src" class="channel-logo__img" :src="logo.src" :alt="logo.label">
          <span v-else class="channel-logo__text">{{ logo.label }}</span>
        </span>
      </div>
    </div>
    <div class="pay-method-card__radio">
      <van-icon v-if="selected && method !== 'COD'" name="success" />
      <span v-if="selected && method === 'COD'"></span>
    </div>
  </div>
</template>

<script>
import { Icon } from 'vant';
import danaLogo from '@/assets/img/pay/Dana_logo.png';
import gopayLogo from '@/assets/img/pay/Logo_Gopay.svg.png';
import linkAjaLogo from '@/assets/img/pay/LinkAja_logo.webp';
import ovoLogo from '@/assets/img/pay/Logo_ovo_purple.svg.png';
import shopeePayLogo from '@/assets/img/pay/shopee-payo.png';

const PAY_CHANNEL_LOGOS = {
  gopay: gopayLogo,
  ovo: ovoLogo,
  dana: danaLogo,
  linkaja: linkAjaLogo,
  shopeepay: shopeePayLogo
};

export default {
  name: 'PayMethodCard',
  components: {
    'van-icon': Icon
  },
  props: {
    method: {
      type: String,
      required: true
    },
    selected: {
      type: Boolean,
      default: false
    },
    recommended: {
      type: Boolean,
      default: false
    },
    recommendedText: {
      type: String,
      default: ''
    },
    showIcon: {
      type: Boolean,
      default: true
    },
    iconType: {
      type: String,
      default: ''
    },
    tags: {
      type: Array,
      default: () => []
    },
    icon: {
      type: String,
      default: 'paid'
    },
    title: {
      type: String,
      default: ''
    },
    desc: {
      type: String,
      default: ''
    },
    logos: {
      type: Array,
      default: () => []
    },
    accent: {
      type: String,
      default: '#2547F1'
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    handleClick() {
      if (this.disabled) return;
      this.$emit('click');
    },
    tagIconName(tag) {
      const map = { VA: 'balance-o', 'E-wallet': 'card', QRIS: 'qr' };
      return map[tag] || '';
    }
  },
  computed: {
    resolvedIconType() {
      if (this.iconType) return this.iconType;
      return this.method === 'COD' ? 'cod' : 'qris';
    },
    cardClass() {
      return {
        'pay-method-card--selected': this.selected,
        'pay-method-card--cod': this.method === 'COD',
        'pay-method-card--store': this.method === 'STORE',
        'pay-method-card--disabled': this.disabled
      };
    },
    logoItems() {
      return (this.logos || []).map(raw => {
        const label = String(raw);
        const key = label.toLowerCase().replace(/\s+/g, '-');
        return {
          raw: label,
          label,
          key,
          src: PAY_CHANNEL_LOGOS[key] || ''
        };
      });
    }
  }
}
</script>

<style lang="less" scoped>
.pay-method-card {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 146px;
  padding: 24px 34px 24px 26px;
  border: 2px solid #E1E5EE;
  border-radius: 22px;
  background: #FFFFFF;
  box-sizing: border-box;

  & + & {
    margin-top: 18px;
  }

  &--selected {
    border-color: #1664F5;
  }

  &--disabled {
    border-color: #E5E8F0;
    background: #F8FAFD;
    opacity: .62;
    cursor: not-allowed;
  }

  &--cod.pay-method-card--selected {
    border-color: #FF7900;
    background: linear-gradient(100deg, #FFF8ED 0%, #FFFFFF 100%);
  }

  &--disabled.pay-method-card--selected {
    border-color: #D9DEE9;
    background: #F8FAFD;
  }

  &__visual {
    width: 110px;
    flex-shrink: 0;
    text-align: center;
  }

  &__visual-label {
    margin-top: 12px;
    font-size: 26px;
    line-height: 32px;
    font-weight: 700;
    color: #111827;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    margin: 0 auto;
    border-radius: 22px;
    background: #F4F8FF;
    box-shadow: 0 12px 24px rgba(21, 101, 245, 0.12);
  }

  &--cod &__icon {
    background: #FFF2DF;
    box-shadow: 0 12px 24px rgba(255, 121, 0, 0.14);
  }

  &--store &__icon {
    background: #F1EEFF;
    box-shadow: 0 12px 24px rgba(100, 79, 255, 0.14);
  }

  &--store.pay-method-card--selected {
    border-color: #1664F5;
    background: linear-gradient(100deg, #F8F7FF 0%, #FFFFFF 100%);
  }

  &--store {
    min-height: 198px;
    padding-right: 24px;

    .pay-method-card__visual { width: 90px; }
    .pay-method-card__body { margin-left: 14px; padding-right: 50px; }
    .pay-method-card__tag {
      max-width: 100%;
      margin-top: 6px;
      padding: 4px 10px;
      border-radius: 7px;
      background: #EAF2FF;
      color: #1664F5;
      font-size: 24px;
      line-height: 30px;
      white-space: normal;
      box-sizing: border-box;
    }
    .pay-method-card__desc { margin-top: 8px; }
    .pay-method-card__radio { right: 24px; }
  }

  &--cod &__visual {
    display: flex;
    align-items: center;
    justify-content: center;
    transform: none;
  }

  &__body {
    min-width: 0;
    flex: 1;
    margin-left: 24px;
    padding-right: 42px;
  }

  &__header {
    display: flex;
    align-items: center;
  }

  &__title {
    font-size: 30px;
    line-height: 40px;
    font-weight: 700;
    color: #05070F;
  }

  &__tag {
    display: inline-flex;
    align-items: center;
    margin-top: 8px;
    padding: 4px 12px;
    border-radius: 8px;
    background: #E8F0FF;
    color: #1767F3;
    font-size: 24px;
    line-height: 30px;
  }

  &--cod &__tag {
    border: 1px solid #FF7900;
    background: #FFFFFF;
    color: #FF7900;
  }

  &__desc {
    margin-top: 10px;
    font-size: 25px;
    line-height: 34px;
    color: #6D7286;
  }

  &__logos {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    column-gap: 10px;
    margin-top: 14px;
    width: calc(100% + 48px);
    white-space: nowrap;
  }

  &__radio {
    position: absolute;
    right: 34px;
    top: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border: 3px solid #D4D8E3;
    border-radius: 50%;
    color: #FFFFFF;
    font-size: 34px;
    transform: translateY(-50%);
    box-sizing: border-box;
  }

  &--selected &__radio {
    border-color: #1664F5;
    background: #1664F5;
  }

  &--disabled &__radio {
    border-color: #D6DBE6;
    background: #EEF2F8;
    color: #B8BFCD;
  }

  &--cod.pay-method-card--selected &__radio {
    border-color: #FF7900;
    background: #FF7900;

    span {
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: #FFFFFF;
    }
  }

  &--disabled.pay-method-card--selected &__radio {
    border-color: #D6DBE6;
    background: #EEF2F8;

    span {
      background: #B8BFCD;
    }
  }
}

.qris-icon {
  display: grid;
  grid-template-columns: repeat(7, 6px);
  grid-template-rows: repeat(7, 6px);
  gap: 3px;
  padding: 5px;
  border-radius: 8px;
  background: #FFFFFF;

  span {
    border-radius: 1px;
    background: transparent;

    &:nth-child(1),
    &:nth-child(2),
    &:nth-child(3),
    &:nth-child(5),
    &:nth-child(6),
    &:nth-child(7),
    &:nth-child(8),
    &:nth-child(10),
    &:nth-child(12),
    &:nth-child(14),
    &:nth-child(15),
    &:nth-child(16),
    &:nth-child(17),
    &:nth-child(19),
    &:nth-child(20),
    &:nth-child(21),
    &:nth-child(23),
    &:nth-child(25),
    &:nth-child(27),
    &:nth-child(29),
    &:nth-child(30),
    &:nth-child(32),
    &:nth-child(33),
    &:nth-child(36),
    &:nth-child(37),
    &:nth-child(38),
    &:nth-child(40),
    &:nth-child(41),
    &:nth-child(43),
    &:nth-child(44),
    &:nth-child(46),
    &:nth-child(48),
    &:nth-child(49) {
      background: #2676F3;
    }
  }
}

.channel-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 64px;
  height: 38px;
  color: #6D7286;
  font-size: 24px;
  line-height: 28px;
  font-weight: 550;

  &__img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &--gopay { width: 32px; height: 34px;transform: scale(1.2) }
  &--gopay &__img {
    width: auto;
    height: 28px;
    max-width: 32px;
  }
  &--ovo { width: 58px; height: 38px; }
  &--dana { width: 66px; height: 38px; }
  &--linkaja { width: 44px; height: 38px; }
  &--shopeepay { width: 54px; height: 38px; transform: scale(1.6)}
  &--dan-lainnya {
    justify-content: flex-start;
    width: auto;
    font-size: 23px;
  }
}

.box-icon {
  position: relative;
  width: 52px;
  height: 48px;
  border-radius: 8px;
  background: linear-gradient(135deg, #FFA438 0%, #FF7900 100%);
  transform: translateY(10px) perspective(80px) rotateX(0deg);

  &::before {
    position: absolute;
    content: '';
    left: 8px;
    right: 8px;
    top: -12px;
    height: 22px;
    border-radius: 8px 8px 2px 2px;
    background: #FFB45F;
    transform: skewX(-24deg);
  }

  &::after {
    position: absolute;
    content: '';
    top: 8px;
    left: 24px;
    width: 5px;
    height: 34px;
    border-radius: 5px;
    background: rgba(255, 255, 255, 0.40);
  }

  span {
    position: absolute;
    right: 8px;
    bottom: 9px;
    width: 12px;
    height: 8px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.58);
  }
}
.wallet-icon {
  position: relative;
  width: 52px;
  height: 40px;
  border: 3px solid #2676F3;
  border-radius: 8px;
  background: #FFFFFF;
  box-sizing: border-box;

  &::before {
    content: '';
    position: absolute;
    right: -7px;
    top: 50%;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #2676F3;
    transform: translateY(-50%);
  }

  span {
    position: absolute;
    left: 8px;
    top: 50%;
    width: 22px;
    height: 3px;
    border-radius: 2px;
    background: #2676F3;
    transform: translateY(-50%);
  }
}

.store-icon {
  position: relative;
  width: 54px;
  height: 40px;
  border: 4px solid #7557FF;
  border-radius: 6px;
  box-sizing: border-box;

  &::before {
    position: absolute;
    content: '';
    left: -7px;
    top: -16px;
    width: 52px;
    height: 18px;
    border-radius: 6px 6px 2px 2px;
    background: repeating-linear-gradient(90deg, #7557FF 0 9px, #FFFFFF 9px 15px);
    border: 4px solid #7557FF;
    box-sizing: border-box;
  }

  span {
    position: absolute;
    left: 18px;
    bottom: 0;
    width: 12px;
    height: 21px;
    border: 3px solid #7557FF;
    border-bottom: 0;
    border-radius: 5px 5px 0 0;
    box-sizing: border-box;
  }
}

.pay-method-card__tags {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  column-gap: 10px;
  margin-top: 14px;
}

.pay-method-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 8px;
  background: #EDF3FF;
  color: #111827;
  font-size: 22px;
  line-height: 30px;

  &__icon {
    font-size: 24px;
    color: #1C63F2;
  }
}
</style>
